/**
 * The DOM overlay, assembled.
 *
 * Owns four panels — resource bar, build hotbar, minimap, selected-hex
 * inspector — plus the end screen, and is the only thing `main.ts` talks to.
 * Plain DOM and CSS, per docs/02: a framework would fight the render loop for
 * the frame budget, and these panels are not complex enough to earn it.
 *
 * The one rule everything here is organised around: **`update` runs at the
 * simulation's 10Hz and must not build DOM.** Nodes are created once, in the
 * constructor; `update` writes `textContent`, toggles classes, and blits a
 * minimap. The exception is the end screen, which builds once per session.
 *
 * Nothing in this file decides a game outcome. Costs and affordability come
 * from `checkClaim`, `expansionCost` and `canAfford` in `kngdm-shared` — the
 * same functions the server authorises with — so the HUD previews the server's
 * answer rather than forming its own opinion.
 */

import type {
  GodTool,
  Biome,
  BuildingType,
  ClaimCheck,
  ClientIntent,
  HexCoord,
  HexState,
  KingdomId,
  KingdomState,
  MapState,
  ResourceType,
  SessionEndedMessage,
} from "kngdm-shared";
import {
  BUILDINGS,
  RESOURCE_TYPES,
  checkClaim,
  clamp01,
  hexKey,
  neighbors,
} from "kngdm-shared";

import { BuildHotbar } from "./buildHotbar";
import { HexTooltip } from "./hexTooltip";
import { GodToolBar } from "./godToolBar";
import type { KingdomSummary } from "./derive";
import { deriveKingdomView } from "./derive";
import { EndScreen } from "./endScreen";
import { formatAmount, formatClock, formatPercent } from "./format";
import { Minimap } from "./minimap";
import { ResourceBar } from "./resourceBar";

export interface HudCallbacks {
  onIntent(intent: ClientIntent): void;
  onJumpToWorld(p: { x: number; y: number }): void;
  onBuildModeChange(b: BuildingType | null): void;
  onGodToolChange(t: GodTool | null): void;
}

export interface HudView {
  readonly tick: number;
  readonly sessionLengthTicks: number;
  readonly tickRateHz: number;
  readonly map: MapState;
  readonly kingdoms: readonly KingdomState[];
  readonly ownKingdomId: KingdomId;
  readonly hexSize: number;
  readonly cameraRect: { topLeft: { x: number; y: number }; bottomRight: { x: number; y: number } };
  readonly selected: HexCoord | null;
  readonly buildMode: BuildingType | null;
}

export class Hud {
  private readonly root: HTMLElement;
  private readonly callbacks: HudCallbacks;

  private readonly resourceBar: ResourceBar;
  private readonly hotbar: BuildHotbar;
  private readonly godTools: GodToolBar;
  /**
   * Owns the position of both hotbars.
   *
   * They used to position themselves with `left: 50%; transform: translateX(-50%)`
   * and magic pixel offsets — which broke the moment panels gained an entrance
   * animation, because the keyframes animate `transform` and silently clobbered
   * the centring. The bar then sat 500px wide starting at the screen centre,
   * overlapping the god tools and the minimap.
   *
   * A flex parent fixes both problems at once: positioning lives here, animation
   * lives on the panels, and the two never touch.
   */
  private readonly dock: HTMLDivElement;
  /**
   * Replaces the fixed inspector panel.
   *
   * That panel required a click and then a look away to the screen corner —
   * so expansion, the central decision in the game, was made blind: the cost of
   * a hex only appeared after you had already committed attention to it.
   */
  readonly tooltip: HexTooltip;
  private readonly minimap: Minimap;
  private readonly inspector: Inspector;
  private readonly endScreen: EndScreen;

  /**
   * Hex lookup, rebuilt when the map object changes identity.
   *
   * The client replaces `MapState` on every delta, so this is effectively a
   * per-tick rebuild of ~1600 entries — cheap, and it buys O(1) lookup for the
   * selected hex and its six neighbours instead of a scan per panel.
   */
  private hexIndex = new Map<string, HexState>();
  private indexedMap: MapState | null = null;

  constructor(root: HTMLElement, callbacks: HudCallbacks) {
    this.root = root;
    this.callbacks = callbacks;

    this.resourceBar = new ResourceBar({
      onWorkerRatioChange: (workerRatio) => {
        this.callbacks.onIntent({ type: "setWorkerTroopSplit", workerRatio });
      },
    });

    this.tooltip = new HexTooltip();

    this.dock = document.createElement("div");
    this.dock.className = "dock";

    this.godTools = new GodToolBar({
      onSelect: (tool) => this.callbacks.onGodToolChange(tool),
    });

    this.hotbar = new BuildHotbar({
      onSelect: (building) => this.callbacks.onBuildModeChange(building),
    });

    this.minimap = new Minimap({
      onJumpToWorld: (point) => this.callbacks.onJumpToWorld(point),
    });

    this.inspector = new Inspector();
    this.endScreen = new EndScreen();

    this.dock.append(this.hotbar.element, this.godTools.element);

    this.root.append(
      this.resourceBar.element,
      this.dock,
      this.tooltip.element,
      this.minimap.element,
      this.endScreen.element,
    );
  }

  update(view: HudView): void {
    this.reindex(view.map);

    const summary = deriveKingdomView(view.map, view.kingdoms, view.ownKingdomId);

    this.resourceBar.update(
      summary,
      formatClock(view.sessionLengthTicks - view.tick, view.tickRateHz),
    );
    this.hotbar.update(summary.banked);
    this.godTools.update(summary.banked);
    this.hotbar.setActive(view.buildMode);
    this.minimap.update({
      map: view.map,
      kingdoms: view.kingdoms,
      ownKingdomId: view.ownKingdomId,
      hexSize: view.hexSize,
      cameraRect: view.cameraRect,
    });

    const selected = view.selected === null ? undefined : this.hexIndex.get(hexKey(view.selected));
    this.inspector.update({
      coord: view.selected,
      hex: selected ?? null,
      claim: selected === undefined ? null : this.checkClaimAt(selected, summary, view),
      kingdoms: view.kingdoms,
      ownKingdomId: view.ownKingdomId,
      summary,
    });
  }

  /**
   * Reflects a build-mode change that came from somewhere else — the 1-9 keys,
   * which `inputController.ts` owns. Does not call back: this is the inbound
   * half of the loop, and re-emitting would bounce the change forever.
   */
  setGodTool(tool: GodTool | null): void {
    this.godTools.setActive(tool);
  }

  setBuildMode(building: BuildingType | null): void {
    this.hotbar.setActive(building);
  }

  showEnd(
    message: SessionEndedMessage,
    kingdoms: readonly KingdomState[],
    ownKingdomId: KingdomId,
  ): void {
    this.endScreen.show(message, kingdoms, ownKingdomId);
  }

  destroy(): void {
    this.resourceBar.destroy();
    this.hotbar.destroy();
    this.godTools.destroy();
    this.minimap.destroy();
    this.inspector.destroy();
    this.endScreen.destroy();
    this.hexIndex.clear();
    this.indexedMap = null;
  }

  private reindex(map: MapState): void {
    if (map === this.indexedMap) return;
    this.indexedMap = map;
    this.hexIndex.clear();
    for (const hex of map.hexes) this.hexIndex.set(hexKey(hex.coord), hex);
  }

  /**
   * What claiming the selected hex would cost and whether it would be allowed.
   *
   * `checkClaim` is the server's own authorisation function; the client calls it
   * to *show* the answer, never to act on it. The claim itself is still an
   * intent the server validates from scratch.
   */
  private checkClaimAt(hex: HexState, summary: KingdomSummary, view: HudView): ClaimCheck {
    const neighbourOwners = new Map<string, KingdomId | null>();
    for (const neighbour of neighbors(hex.coord)) {
      const key = hexKey(neighbour);
      neighbourOwners.set(key, this.hexIndex.get(key)?.owner ?? null);
    }

    return checkClaim(hex.coord, {
      biome: hex.biome,
      owner: hex.owner,
      neighbourOwners,
      claimantId: view.ownKingdomId,
      claimantResources: summary.banked,
      claimantTerritoryCount: summary.territory,
      claimantEliminated: summary.eliminated,
    });
  }
}

// ---------------------------------------------------------------------------
// Selected-hex inspector
// ---------------------------------------------------------------------------

interface InspectorView {
  readonly coord: HexCoord | null;
  readonly hex: HexState | null;
  readonly claim: ClaimCheck | null;
  readonly kingdoms: readonly KingdomState[];
  readonly ownKingdomId: KingdomId;
  readonly summary: KingdomSummary;
}

const BIOME_LABELS: Record<Biome, string> = {
  plains: "Plains",
  forest: "Forest",
  hills: "Hills",
  mountains: "Mountains",
  desert: "Desert",
  tundra: "Tundra",
};

const RESOURCE_INITIALS: Record<ResourceType, string> = {
  food: "F",
  timber: "T",
  ore: "O",
  aether: "Æ",
  gold: "G",
};

/**
 * Everything about the hex under the cursor's last click, including what taking
 * it would cost.
 *
 * The claim line does double duty as the game's only tutorial: a hex held by a
 * rival says so and says that troops, not resources, are how it changes hands —
 * which is the single most important thing a new player has to learn about
 * `NEUTRAL_LAND_CAPTURABLE_BY_PRESSURE` being false.
 */
class Inspector {
  readonly element: HTMLElement;

  private readonly title: HTMLElement;
  private readonly empty: HTMLElement;
  private readonly body: HTMLElement;
  private readonly biome: HTMLElement;
  private readonly owner: HTMLElement;
  private readonly troops: HTMLElement;
  private readonly building: HTMLElement;
  private readonly captureRow: HTMLElement;
  private readonly captureValue: HTMLElement;
  private readonly captureFill: HTMLElement;
  private readonly claimNote: HTMLElement;
  private readonly claimCost: HTMLElement;
  private readonly costChips: ReadonlyMap<ResourceType, HTMLElement>;

  constructor() {
    this.element = el("section", "panel panel--inspect");
    this.title = el("h2", undefined, "Hex");
    this.empty = el("p", "inspect__empty", "Click a hex to inspect it.");

    this.body = el("div", "inspect__body is-hidden");
    const list = el("dl");
    this.biome = appendRow(list, "Biome");
    this.owner = appendRow(list, "Owner");
    this.troops = appendRow(list, "Troops");
    this.building = appendRow(list, "Building");

    this.captureRow = el("div", "inspect__capture is-hidden");
    const captureHead = el("div", "inspect__capture-head");
    captureHead.append(el("span", undefined, "Capture"));
    this.captureValue = el("span", "inspect__capture-value", "0%");
    captureHead.append(this.captureValue);
    const captureTrack = el("div", "inspect__capture-track");
    this.captureFill = el("div", "inspect__capture-fill");
    captureTrack.append(this.captureFill);
    this.captureRow.append(captureHead, captureTrack);

    this.claimCost = el("div", "inspect__cost");
    const chips = new Map<ResourceType, HTMLElement>();
    for (const type of RESOURCE_TYPES) {
      const chip = el("span", "inspect__chip is-hidden");
      chips.set(type, chip);
      this.claimCost.append(chip);
    }
    this.costChips = chips;

    this.claimNote = el("p", "inspect__note", "");

    this.body.append(list, this.captureRow, this.claimCost, this.claimNote);
    this.element.append(this.title, this.empty, this.body);
  }

  update(view: InspectorView): void {
    const hex = view.hex;
    if (view.coord === null || hex === null) {
      this.empty.classList.remove("is-hidden");
      this.body.classList.add("is-hidden");
      setText(this.title, "Hex");
      return;
    }

    this.empty.classList.add("is-hidden");
    this.body.classList.remove("is-hidden");

    setText(this.title, `Hex ${view.coord.q}, ${view.coord.r}`);
    setText(this.biome, BIOME_LABELS[hex.biome]);
    setText(this.owner, ownerLabel(hex.owner, view));
    setText(this.troops, formatAmount(hex.troops));
    setText(this.building, hex.building === null ? "—" : BUILDINGS[hex.building].label);

    // The meter only exists while someone is actually pushing; showing a 0%
    // bar on every quiet hex would make the panel look like a war zone.
    const contested = hex.capture > 0.001;
    this.captureRow.classList.toggle("is-hidden", !contested);
    if (contested) {
      setText(this.captureValue, formatPercent(hex.capture));
      this.captureFill.style.width = `${Math.round(clamp01(hex.capture) * 100)}%`;
    }

    this.updateClaim(view, hex);
  }

  destroy(): void {
    this.element.remove();
  }

  private updateClaim(view: InspectorView, hex: HexState): void {
    const claim = view.claim;
    if (claim === null) {
      this.claimCost.classList.add("is-hidden");
      setText(this.claimNote, "");
      return;
    }

    const owned = hex.owner === view.ownKingdomId;
    const showCost = !owned && hex.owner === null;
    this.claimCost.classList.toggle("is-hidden", !showCost);

    if (showCost) {
      for (const type of RESOURCE_TYPES) {
        const chip = this.costChips.get(type);
        if (chip === undefined) continue;
        const amount = claim.cost[type];
        chip.classList.toggle("is-hidden", amount <= 0);
        if (amount <= 0) continue;
        setText(chip, `${formatAmount(amount)}${RESOURCE_INITIALS[type]}`);
        chip.classList.toggle("is-missing", view.summary.banked[type] < amount);
      }
    }

    setText(this.claimNote, claimNote(claim, owned));
    this.claimNote.classList.toggle("is-good", claim.allowed);
    this.claimNote.classList.toggle(
      "is-bad",
      !claim.allowed && claim.reason === "insufficient-resources",
    );
  }
}

function claimNote(claim: ClaimCheck, owned: boolean): string {
  if (claim.allowed) return "Click to claim.";
  switch (claim.reason) {
    case "already-owned":
      return owned
        ? "Your territory."
        : "Held by a rival — take it with troop pressure, not resources.";
    case "not-adjacent":
      return "Not adjacent to your territory.";
    case "insufficient-resources":
      return "You cannot afford this claim.";
    case "eliminated":
      return "Your kingdom has been eliminated.";
    case "off-map":
    case null:
      return "";
  }
}

function ownerLabel(owner: KingdomId | null, view: InspectorView): string {
  if (owner === null) return "Unclaimed";
  if (owner === view.ownKingdomId) return "You";
  return view.kingdoms.find((kingdom) => kingdom.id === owner)?.name ?? owner;
}

// ---------------------------------------------------------------------------
// DOM helpers
// ---------------------------------------------------------------------------

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function setText(node: HTMLElement, value: string): void {
  if (node.textContent !== value) node.textContent = value;
}

/** Appends a `dt`/`dd` pair and hands back the `dd` to write into later. */
function appendRow(list: HTMLElement, label: string): HTMLElement {
  const value = el("dd", undefined, "—");
  list.append(el("dt", undefined, label), value);
  return value;
}
